import React, { useEffect, useMemo, useRef, useState } from 'react'
import { connect, useDispatch } from 'react-redux'
import moment from 'moment'
import styles from './MenuPanel.module.css'
import DishCard from './DishCard'
import AddDishView from './AddDishView'
import ConfirmDialog from './ConfirmDialog'
import { menuItemRemove, menuRequestStart } from '../../redux/actions/Menu'
import { removeDishFromBusinessLunch, setMenuAvailable } from '../../api/Menu'
import { IconBack, IconBurger, IconChevron, IconPlus, IconSearch } from './icons'

const mapStateToProps = ({ menuData }) => ({
  menu: menuData.menu,
  businessMenu: menuData.businessMenu,
})

// API отдаёт dish.section_id = 0 для ВСЕХ блюд независимо от категории,
// поэтому группировать нужно по section_title/section_sort_order, а не по section_id.
// dish.section_id всё же сохраняем отдельным полем (sectionId) — он уходит в запрос
// "добавить блюдо в секцию", менять этот payload не будем без бэкенда.
const groupBySection = menu => {
  const map = new Map()
  menu.forEach(dish => {
    const key = `${dish.section_sort_order}_${dish.section_title}`
    if (!map.has(key)) {
      map.set(key, { id: key, title: dish.section_title, sortOrder: dish.section_sort_order, sectionId: dish.section_id, dishes: [] })
    }
    map.get(key).dishes.push(dish)
  })
  return Array.from(map.values()).sort((a, b) => a.sortOrder - b.sortOrder)
}

const matchesTerm = (dish, term) => !term || (dish.title_ru || '').toLowerCase().includes(term)

const MenuPanel = ({ open, onClose, menu, businessMenu }) => {
  const dispatch = useDispatch()
  const pathname = window.location.pathname?.split('/')[1]
  const date = moment().format('YYYY-MM-DD')
  const [mode, setMode] = useState('list')
  const [addTarget, setAddTarget] = useState(null)
  const [search, setSearch] = useState('')
  const [collapsed, setCollapsed] = useState({})
  const [headerHidden, setHeaderHidden] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [deleting, setDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState(false)

  const bodyRef = useRef(null)
  const lastScrollRef = useRef(0)

  useEffect(() => {
    if (!open) {
      setMode('list')
      setAddTarget(null)
      setSearch('')
      setCollapsed({})
      setHeaderHidden(false)
      setDeleteTarget(null)
      setDeleting(false)
      setDeleteError(false)
    }
  }, [open])

  const term = search.trim().toLowerCase()

  // При активном поиске раскрываем всё принудительно, чтобы результаты были видны,
  // но заголовки категорий/подкатегорий сохраняем.
  const isCollapsed = id => !term && !!collapsed[id]
  const toggleCollapsed = id => setCollapsed(prev => ({ ...prev, [id]: !prev[id] }))

  // existingIds для окна "добавить блюдо" считаем по полному списку секции,
  // а не по отфильтрованному поиском — иначе при активном поиске исключения потеряются.
  const sections = useMemo(() => {
    return groupBySection(menu)
      .map(section => ({ ...section, allIds: section.dishes.map(d => d.id), dishes: section.dishes.filter(dish => matchesTerm(dish, term)) }))
      .filter(section => !term || section.dishes.length > 0)
  }, [menu, term])

  const business = useMemo(() => {
    return businessMenu
      .map(bl => ({
        ...bl,
        categories: bl.categories
          .map(category => ({ ...category, allIds: category.menu.map(d => d.id), menu: category.menu.filter(dish => matchesTerm(dish, term)) }))
          .filter(category => !term || category.menu.length > 0),
      }))
      .filter(bl => !term || bl.categories.length > 0)
  }, [businessMenu, term])

  // Прячем шапку с поиском при прокрутке вниз и показываем при прокрутке вверх.
  // Слушаем нативный scroll на .body, а не React onScroll — так надёжнее.
  useEffect(() => {
    const el = bodyRef.current
    if (!el) return undefined
    const onScroll = () => {
      const y = el.scrollTop
      const last = lastScrollRef.current
      if (y <= 8) setHeaderHidden(false)
      else if (y > last + 6) setHeaderHidden(true)
      else if (y < last - 6) setHeaderHidden(false)
      lastScrollRef.current = y
    }
    el.addEventListener('scroll', onScroll, { passive: true })
    return () => el.removeEventListener('scroll', onScroll)
  }, [])

  const openAddView = target => {
    setAddTarget(target)
    setMode('add')
  }

  const closeAddView = () => {
    setMode('list')
    setAddTarget(null)
  }

  // после добавления/возврата блюда перечитываем меню, чтобы панель обновилась
  // сразу, не дожидаясь MQTT refresh (который может и не дойти)
  const finishAddView = () => {
    dispatch(menuRequestStart({ restId: pathname, date }))
    closeAddView()
  }

  const closeDeleteDialog = () => {
    setDeleteTarget(null)
    setDeleteError(false)
  }

  const confirmDelete = async () => {
    if (!deleteTarget) return
    const { dish, kind, blId, categoryId } = deleteTarget
    setDeleting(true)
    setDeleteError(false)
    try {
      if (kind === 'blCategory') {
        await removeDishFromBusinessLunch(pathname, blId, dish.id)
        dispatch(menuItemRemove({ menuId: dish.id, blCategoryId: categoryId }))
      } else {
        const response = await setMenuAvailable(pathname, dish.id, false)
        if (response?.data?.data == null) {
          // блюда с таким id уже нет — состояние рассинхронено, перечитываем меню
          dispatch(menuRequestStart({ restId: pathname, date }))
        } else {
          dispatch(menuItemRemove({ menuId: dish.id }))
        }
      }
      // MQTT пришлёт refresh — он подхватит изменение и у остальных клиентов.
      setDeleteTarget(null)
    } catch (error) {
      console.error('hideDish failed', error.response?.status, error.response?.data || error.message)
      setDeleteError(true)
    } finally {
      setDeleting(false)
    }
  }

  return (
    <>
    <div className={`${styles.panel} ${open ? styles.open : ''}`}>
      <div className={styles.header}>
        {mode === 'add' ? (
          <button className={styles.backBtn} onClick={closeAddView}>
            <IconBack />
          </button>
        ) : null}
        <h2 className={styles.title}>{mode === 'add' ? 'Добавить блюдо' : 'Меню'}</h2>
        <button className={styles.burgerBtn} onClick={onClose}>
          <IconBurger />
        </button>
      </div>

      {mode === 'add' ? null : (
        <div className={`${styles.searchBar} ${headerHidden ? styles.searchBarHidden : ''}`}>
          <div className={styles.searchInputWrap}>
            <input
              className={styles.searchInput}
              placeholder='Поиск блюда'
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {search ? (
              <button className={styles.searchClear} onClick={() => setSearch('')} title='Очистить'>
                ✕
              </button>
            ) : (
              <IconSearch className={styles.searchIcon} />
            )}
          </div>
        </div>
      )}

      <div className={styles.body} ref={bodyRef}>
        {mode === 'add' ? (
          <AddDishView restId={pathname} date={date} target={addTarget} onDone={finishAddView} />
        ) : (
          <>
            {sections.map(section => {
              const headerId = `sec_${section.id}`
              const hidden = isCollapsed(headerId)
              return (
                <div className={styles.section} key={`section_${section.id}`}>
                  <div className={styles.sectionHeader}>
                    <h3 onClick={() => toggleCollapsed(headerId)}>{section.title}</h3>
                    <div className={styles.headerActions}>
                      <button
                        className={styles.addBtn}
                        onClick={() =>
                          openAddView({ kind: 'section', sectionId: section.sectionId, sectionTitle: section.title, existingIds: section.allIds })
                        }
                      >
                        <IconPlus />
                      </button>
                      <button className={styles.collapseBtn} onClick={() => toggleCollapsed(headerId)} title={hidden ? 'Развернуть' : 'Свернуть'}>
                        <IconChevron className={hidden ? styles.chevronCollapsed : ''} />
                      </button>
                    </div>
                  </div>
                  {!hidden &&
                    section.dishes.map(dish => (
                      <DishCard
                        key={dish.id}
                        dish={dish}
                        restId={pathname}
                        date={date}
                        onRequestDelete={d => setDeleteTarget({ kind: 'section', dish: d })}
                      />
                    ))}
                </div>
              )
            })}

            {business.map(bl => {
              const blId = `bl_${bl.id}`
              const blHidden = isCollapsed(blId)
              return (
                <div className={styles.businessLunch} key={`bl_${bl.id}`}>
                  <div className={styles.blHeader}>
                    <h2 className={styles.blTitle} onClick={() => toggleCollapsed(blId)}>
                      {bl.title_ru || bl.title || bl.name || 'Бизнес-ланч'}
                    </h2>
                    <button className={styles.collapseBtn} onClick={() => toggleCollapsed(blId)} title={blHidden ? 'Развернуть' : 'Свернуть'}>
                      <IconChevron className={blHidden ? styles.chevronCollapsed : ''} />
                    </button>
                  </div>
                  {!blHidden &&
                    bl.categories.map(category => {
                      const catId = `blcat_${bl.id}_${category.id}`
                      const catHidden = isCollapsed(catId)
                      return (
                        <div className={styles.section} key={`bl_${bl.id}_cat_${category.id}`}>
                          <div className={styles.sectionHeader}>
                            <h3 onClick={() => toggleCollapsed(catId)}>{category.category}</h3>
                            <div className={styles.headerActions}>
                              <button
                                className={styles.addBtn}
                                onClick={() =>
                                  openAddView({
                                    kind: 'blCategory',
                                    blId: bl.bid,
                                    categoryId: category.id,
                                    categoryTitle: category.category,
                                    existingIds: category.allIds,
                                  })
                                }
                              >
                                <IconPlus />
                              </button>
                              <button className={styles.collapseBtn} onClick={() => toggleCollapsed(catId)} title={catHidden ? 'Развернуть' : 'Свернуть'}>
                                <IconChevron className={catHidden ? styles.chevronCollapsed : ''} />
                              </button>
                            </div>
                          </div>
                          {!catHidden &&
                            category.menu.map(dish => (
                              <DishCard
                                key={dish.id}
                                dish={dish}
                                restId={pathname}
                                date={date}
                                onRequestDelete={d => setDeleteTarget({ kind: 'blCategory', dish: d, blId: bl.bid, categoryId: category.id })}
                              />
                            ))}
                        </div>
                      )
                    })}
                </div>
              )
            })}

            {term && sections.length === 0 && business.length === 0 && <div className={styles.empty}>Ничего не найдено</div>}
          </>
        )}
      </div>
    </div>

    {deleteTarget && (
      <ConfirmDialog
        text={
          deleteTarget.kind === 'blCategory'
            ? 'Убрать блюдо из этого бизнес-ланча?'
            : 'Убрать блюдо из меню? Вернуть его можно через «+».'
        }
        error={deleteError}
        busy={deleting}
        onConfirm={confirmDelete}
        onCancel={closeDeleteDialog}
      />
    )}
    </>
  )
}

export default connect(mapStateToProps)(MenuPanel)
